import React, { useEffect, useMemo, useState } from "react"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { toast } from "react-toastify"
import { marketingMilestoneEvents, marketingMilestoneRules } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { mapListToCamelCase, mapPaginatedDataToCamelCase } from "../../../../utility"
import { MarketingMilestoneEvent, MarketingMilestoneRule, PaginatedData } from "../../../../types"
import Loading from "../../../ui/Loading"
import NotFound from "../../../NotFound"
import { DailyTrendsIcon } from "../../../ui/icons"
import MarketingMilestoneRulesDrawer from "./MarketingMilestoneRulesDrawer"
import MarketingTopTracksTab from "./MarketingTopTracksTab"
import SendArtistPushModal from "./SendArtistPushModal"
import TrackedUsersModal from "./TrackedUsersModal"

type Tab = "notifications" | "lists"

const EVENT_TYPES = [
  { label: "All events", value: "" },
  { label: "Track stream milestone", value: "track_stream_milestone" },
  { label: "Artist stream milestone", value: "artist_stream_milestone" },
  { label: "Daily peak", value: "daily_peak" },
  { label: "Weekly peak", value: "weekly_peak" },
  { label: "Monthly peak", value: "monthly_peak" },
  { label: "Fast growth", value: "fast_growth" },
  { label: "Internal Stream Playlist", value: "playlist_entry" },
  { label: "Playlist growth spike", value: "playlist_growth_spike" },
  { label: "Chart entry", value: "chart_entry" },
  { label: "Chart climb", value: "chart_position_increase" },
  { label: "Shazam spike", value: "shazam_spike" },
  { label: "Country breakout", value: "country_breakout" }
]

// Total lifetime stats sourced from Chartmetric (cm_statistics), refreshed weekly by the backend cron.
const GLOBAL_STREAM_EVENT_TYPES = [
  { label: "Track total (Spotify/YouTube)", value: "track_total" },
  { label: "Artist total (Spotify/YouTube)", value: "artist_total" }
]

const STATUSES = [
  { label: "New", value: "new" },
  { label: "Reviewed", value: "reviewed" },
  { label: "Dismissed", value: "dismissed" }
]

type SummaryBucket = { count: number; [key: string]: any }
type Summary = {
  total: number
  by_status: SummaryBucket[]
  by_event_type: SummaryBucket[]
  by_platform: SummaryBucket[]
  by_country: SummaryBucket[]
}

const formatLabel = (value?: string | null) => {
  if (!value) return "-"
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

const formatNumber = (value?: number | null) => (value === null || value === undefined ? "-" : value.toLocaleString())

const buildQuery = (filters: { [key: string]: string }, limit: number, offset: number) => {
  const params = new URLSearchParams()
  params.set("limit", `${limit}`)
  params.set("offset", `${offset}`)
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return `/?${params.toString()}`
}

const buildSummaryQuery = (filters: { [key: string]: string }) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  const search = params.toString()
  return search ? `/summary/?${search}` : "/summary/"
}

const EventBadge = ({
  children,
  tone = "gray"
}: {
  children: React.ReactNode
  tone?: "green" | "blue" | "red" | "gray" | "yellow" | "violet"
}) => {
  const colors = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue: "bg-sky-50 text-sky-700 border-sky-100",
    red: "bg-red-50 text-red-700 border-red-100",
    gray: "bg-gray-50 text-gray-700 border-gray-100",
    yellow: "bg-amber-50 text-amber-800 border-amber-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100"
  }
  return <span className={`text-xs border rounded-full px-2.5 py-1 ${colors[tone]}`}>{children}</span>
}

const SummaryCard = ({ title, value, hint }: { title: string; value: string | number; hint?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-4 flex-1 min-w-[160px]">
    <p className="text-xs text-gray-400 uppercase tracking-wide">{title}</p>
    <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
)

const eventTypeTone = (eventType: string) => {
  if (eventType.includes("chart")) return "violet"
  if (eventType.includes("playlist")) return "blue"
  if (eventType.includes("total")) return "violet"
  if (eventType.includes("peak") || eventType.includes("milestone")) return "green"
  if (eventType.includes("shazam") || eventType.includes("breakout") || eventType.includes("growth")) return "yellow"
  return "gray"
}

const MarketingMilestones = () => {
  const limit = 20
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [events, setEvents] = useState<MarketingMilestoneEvent[]>([])
  const [rules, setRules] = useState<MarketingMilestoneRule[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [eventType, setEventType] = useState("")
  const [status, setStatus] = useState("new")
  const [platform, setPlatform] = useState("")
  const [country, setCountry] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [search, setSearch] = useState("")
  const [daysBack, setDaysBack] = useState("7")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [rulesOpen, setRulesOpen] = useState(false)
  const [trackedUsersOpen, setTrackedUsersOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>("notifications")
  const [pushModalEvent, setPushModalEvent] = useState<MarketingMilestoneEvent | null>(null)

  const activeRuleCount = useMemo(() => rules.filter((rule) => rule.active).length, [rules])

  const filters = useMemo(
    () => ({
      event_type: eventType,
      status,
      platform,
      country,
      date_from: dateFrom,
      date_to: dateTo,
      search
    }),
    [eventType, status, platform, country, dateFrom, dateTo, search]
  )

  const fetchEvents = (nextOffset = 0, append = false) => {
    setLoading(true)
    marketingMilestoneEvents(buildQuery(filters, limit, nextOffset), "get")
      .then((resp) => {
        const data = mapPaginatedDataToCamelCase<MarketingMilestoneEvent>(resp.data as PaginatedData<any>)
        setEvents(append ? [...events, ...data.results] : data.results)
        setCount(data.count || 0)
        setOffset(nextOffset)
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.detail || "Error fetching marketing milestone events", errorFilledToastOptions)
      })
  }

  const fetchSummary = () => {
    marketingMilestoneEvents(buildSummaryQuery(filters), "get")
      .then((resp) => setSummary(resp.data as Summary))
      .catch(() => undefined)
  }

  const fetchRules = () => {
    marketingMilestoneRules("/?limit=200&offset=0", "get")
      .then((resp) => {
        const raw = resp.data.results || []
        // config is opaque JSON with snake_case keys the backend relies on — keep it as-is
        // (mapToCamelCase would rewrite spotify_thresholds -> spotifyThresholds and break scans).
        setRules(mapListToCamelCase(raw).map((r: any, i: number) => ({ ...r, config: raw[i].config })))
      })
      .catch(() => {
        toast.error("Error fetching milestone rules", errorFilledToastOptions)
      })
  }

  const seedRules = () => {
    setLoading(true)
    marketingMilestoneRules("/seed_defaults/", "post")
      .then((resp) => {
        toast.success(`Default rules ready. Created: ${resp.data.created}`, successFilledToastOptions)
        fetchRules()
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.detail || "Error seeding milestone rules", errorFilledToastOptions)
      })
  }

  const generateEvents = () => {
    setGenerating(true)
    marketingMilestoneEvents("/generate/", "post", {
      days_back: Number(daysBack) || 7,
      send_slack_notifications: false
    })
      .then((resp) => {
        toast.success(`Generated ${resp.data.created} new milestone events`, successFilledToastOptions)
        fetchEvents(0)
        fetchSummary()
        setGenerating(false)
      })
      .catch((err) => {
        setGenerating(false)
        toast.error(err?.response?.data?.detail || "Error generating milestone events", errorFilledToastOptions)
      })
  }

  const updateStatus = (eventId: number, nextStatus: string) => {
    marketingMilestoneEvents(`/${eventId}/status/`, "put", { status: nextStatus })
      .then((resp) => {
        const updated = resp.data
        setEvents(events.map((event) => (event.id === eventId ? { ...event, status: updated.status } : event)))
        toast.success("Event status updated", successFilledToastOptions)
        fetchSummary()
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Error updating event status", errorFilledToastOptions)
      })
  }

  const bulkUpdateStatus = (nextStatus: string) => {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    marketingMilestoneEvents("/bulk_status/", "put", { status: nextStatus, ids })
      .then((resp) => {
        toast.success(`${resp.data.updated} events updated`, successFilledToastOptions)
        setEvents(events.map((event) => (selectedIds.has(event.id) ? { ...event, status: nextStatus } : event)))
        setSelectedIds(new Set())
        fetchSummary()
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Error updating events", errorFilledToastOptions)
      })
  }

  const toggleSelected = (eventId: number) => {
    const next = new Set(selectedIds)
    if (next.has(eventId)) next.delete(eventId)
    else next.add(eventId)
    setSelectedIds(next)
  }

  const copyForSocial = (event: MarketingMilestoneEvent) => {
    const lines: string[] = []
    lines.push(event.title)
    if (event.message && event.message !== event.title) lines.push(event.message)
    if (event.platform) lines.push(`Platform: ${event.platform}`)
    if (event.country) lines.push(`Country: ${event.country}`)
    if (event.dateDetected) lines.push(`Detected: ${event.dateDetected}`)
    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => toast.success("Copied to clipboard", successFilledToastOptions))
      .catch(() => toast.error("Could not copy", errorFilledToastOptions))
  }

  useEffect(() => {
    fetchRules()
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchEvents(0)
      fetchSummary()
    }, 350)
    return () => clearTimeout(timeout)
  }, [eventType, status, platform, country, dateFrom, dateTo, search])

  const statusTone = (value: string): "green" | "yellow" | "red" => {
    if (value === "reviewed") return "green"
    if (value === "dismissed") return "red"
    return "yellow"
  }

  const summaryTopType = summary?.by_event_type?.[0]
  const summaryTopPlatform = summary?.by_platform?.[0]
  const summaryNew = summary?.by_status?.find((row) => row.status === "new")?.count ?? 0

  return (
    <div className="w-full min-h-screen bg-[#FAFAFA]">
      {(loading || generating) && <Loading />}

      {rulesOpen && (
        <MarketingMilestoneRulesDrawer rules={rules} onClose={() => setRulesOpen(false)} onChanged={fetchRules} />
      )}

      <TrackedUsersModal open={trackedUsersOpen} onClose={() => setTrackedUsersOpen(false)} />

      <SendArtistPushModal
        open={pushModalEvent !== null}
        onClose={() => setPushModalEvent(null)}
        event={pushModalEvent}
        onSent={(eventId, sentAt) => {
          setEvents(
            events.map((event) => (event.id === eventId ? { ...event, artistNotificationSentAt: sentAt } : event))
          )
        }}
      />

      <div className="flex flex-row justify-between items-center py-4 px-6 border-b border-gray-100 bg-white">
        <div className="flex flex-row items-center gap-2">
          <div className="w-8 h-8 p-1.5 flex items-center justify-center shrink-0">{DailyTrendsIcon("#111")}</div>
          <div>
            <KTitleSpan
              text={`Marketing Milestones (${summary?.total ?? count})`}
              fontSize={20}
              color="#111"
              lineHeight="28px"
              fontWeight={500}
            />
            <KSpan
              text={`${activeRuleCount} active rules · internal feed only · no artist-facing notifications`}
              color="#666"
              fontSize={12}
            />
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <div className="w-[90px]">
            <KInput value={daysBack} placeholder="Days" onChange={setDaysBack} height={18} />
          </div>
          <KButton
            text="Generate"
            height="36px"
            width="120px"
            background="#111"
            hoverBackground="#333"
            textColor="#FFF"
            onClick={generateEvents}
            disabled={generating}
          />
          <KButton
            text="Tracked Artists"
            height="36px"
            width="140px"
            background="#F5F5F5"
            hoverBackground="#E5E5E5"
            onClick={() => setTrackedUsersOpen(true)}
            disabled={loading}
          />
          <KButton
            text="Manage Rules"
            height="36px"
            width="140px"
            background="#F5F5F5"
            hoverBackground="#E5E5E5"
            onClick={() => setRulesOpen(true)}
            disabled={loading}
          />
          <KButton
            text="Seed Rules"
            height="36px"
            width="120px"
            background="#F5F5F5"
            hoverBackground="#E5E5E5"
            onClick={seedRules}
            disabled={loading}
          />
          <KButton
            text="Refresh"
            height="36px"
            width="100px"
            background="#F5F5F5"
            hoverBackground="#E5E5E5"
            onClick={() => {
              fetchEvents(0)
              fetchSummary()
            }}
            disabled={loading}
          />
        </div>
      </div>

      <div className="px-6 pt-4 border-b border-gray-100 bg-white">
        <div className="flex gap-1">
          <button
            className={`h-10 px-4 text-sm border-b-2 ${
              activeTab === "notifications"
                ? "border-[#111] text-[#111] font-medium"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("notifications")}
          >
            Notifications
          </button>
          <button
            className={`h-10 px-4 text-sm border-b-2 ${
              activeTab === "lists"
                ? "border-[#111] text-[#111] font-medium"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("lists")}
          >
            Lists
          </button>
        </div>
      </div>

      {activeTab === "lists" && <MarketingTopTracksTab />}

      {activeTab === "notifications" && (
        <div className="p-6">
          <div className="flex flex-wrap gap-4 mb-5">
            <SummaryCard title="Total events" value={summary?.total ?? "-"} />
            <SummaryCard title="New" value={summaryNew} hint="Awaiting marketing review" />
            <SummaryCard
              title="Top event"
              value={summaryTopType ? formatLabel(summaryTopType.event_type) : "-"}
              hint={summaryTopType ? `${summaryTopType.count} events` : undefined}
            />
            <SummaryCard
              title="Top platform"
              value={summaryTopPlatform?.platform ? summaryTopPlatform.platform : "-"}
              hint={summaryTopPlatform?.platform ? `${summaryTopPlatform.count} events` : undefined}
            />
            <SummaryCard
              title="Top country"
              value={summary?.by_country?.[0]?.country ? summary.by_country[0].country : "-"}
              hint={summary?.by_country?.[0]?.country ? `${summary.by_country[0].count} events` : undefined}
            />
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3">
            <select
              className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              {EVENT_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <optgroup label="Global All Stream">
                {GLOBAL_STREAM_EVENT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            </select>
            <select
              className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {STATUSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="w-[140px]">
              <KInput value={platform} placeholder="Platform" onChange={setPlatform} height="40px" />
            </div>
            <div className="w-[100px]">
              <KInput value={country} placeholder="Country" onChange={setCountry} height="40px" />
            </div>
            <input
              type="date"
              className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              type="date"
              className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <div className="w-[280px] grow">
              <KInput
                value={search}
                placeholder="Search title, artist, track, ISRC, playlist"
                onChange={setSearch}
                height="40px"
                leftIcon="/search.svg"
              />
            </div>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-2xl px-4 py-2 mb-4">
              <KSpan text={`${selectedIds.size} selected`} fontSize={13} fontWeight={500} color="#92400E" />
              <div className="flex gap-2">
                <KButton
                  text="Mark reviewed"
                  height="32px"
                  width="130px"
                  background="#111"
                  hoverBackground="#333"
                  textColor="#FFF"
                  onClick={() => bulkUpdateStatus("reviewed")}
                />
                <KButton
                  text="Dismiss"
                  height="32px"
                  width="100px"
                  background="#FEE2E2"
                  hoverBackground="#FECACA"
                  textColor="#991B1B"
                  onClick={() => bulkUpdateStatus("dismissed")}
                />
                <KButton
                  text="Clear"
                  height="32px"
                  width="80px"
                  background="#FFF"
                  hoverBackground="#F5F5F5"
                  onClick={() => setSelectedIds(new Set())}
                />
              </div>
            </div>
          )}

          {!loading && events.length === 0 && (
            <div className="flex w-full h-full items-center justify-center pt-[100px]">
              <NotFound
                title="No milestone events"
                description="Generate events after trend and playlist ingestion jobs complete, or adjust filters."
                imageWidth={250}
              />
            </div>
          )}

          {events.length > 0 && (
            <div className="grid gap-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow ${
                    selectedIds.has(event.id) ? "border-amber-300" : "border-gray-100"
                  }`}
                >
                  <div className="flex justify-between gap-6">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="mt-1.5"
                        checked={selectedIds.has(event.id)}
                        onChange={() => toggleSelected(event.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <EventBadge tone={statusTone(event.status)}>{formatLabel(event.status)}</EventBadge>
                          <EventBadge tone={eventTypeTone(event.eventType)}>{formatLabel(event.eventType)}</EventBadge>
                          <EventBadge>{event.platform || "unknown platform"}</EventBadge>
                          {event.country && <EventBadge tone="green">{event.country}</EventBadge>}
                          {event.playlistType && <EventBadge>{formatLabel(event.playlistType)}</EventBadge>}
                        </div>
                        <KTitleSpan text={event.title} fontSize={17} lineHeight="24px" fontWeight={600} color="#111" />
                        {event.message && event.message !== event.title && (
                          <p className="text-sm text-gray-600 mt-1">{event.message}</p>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                          <div>
                            <p className="text-gray-400">Artist</p>
                            <p className="text-gray-800 truncate">{event.artistName || "-"}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Track</p>
                            <p className="text-gray-800 truncate">{event.trackTitle || event.isrc || "-"}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Playlist / Chart</p>
                            <p className="text-gray-800 truncate">{event.playlistName || event.source || "-"}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Detected</p>
                            <p className="text-gray-800">{event.dateDetected || "-"}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
                          <span>Metric: {formatNumber(event.metricValue)}</span>
                          <span>Previous: {formatNumber(event.previousValue)}</span>
                          <span>Threshold: {formatNumber(event.thresholdValue)}</span>
                          <span>Position: {formatNumber(event.position)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {event.status !== "reviewed" && (
                        <KButton
                          text="Mark reviewed"
                          height="34px"
                          width="130px"
                          background="#111"
                          hoverBackground="#333"
                          textColor="#FFF"
                          onClick={() => updateStatus(event.id, "reviewed")}
                        />
                      )}
                      {event.status !== "dismissed" && (
                        <KButton
                          text="Dismiss"
                          height="34px"
                          width="130px"
                          background="#FEE2E2"
                          hoverBackground="#FECACA"
                          textColor="#991B1B"
                          onClick={() => updateStatus(event.id, "dismissed")}
                        />
                      )}
                      <KButton
                        text="Copy"
                        height="34px"
                        width="130px"
                        background="#F5F5F5"
                        hoverBackground="#E5E5E5"
                        onClick={() => copyForSocial(event)}
                      />
                      <div
                        title={
                          event.artistNotificationSentAt
                            ? `Sent: ${new Date(event.artistNotificationSentAt).toLocaleString()}`
                            : "Send notification to artist (web + mobile push)"
                        }
                      >
                        <KButton
                          text={event.artistNotificationSentAt ? "Sent" : "Send Notification"}
                          height="34px"
                          width="130px"
                          background={event.artistNotificationSentAt ? "#ECFDF5" : "#E0F2FE"}
                          hoverBackground={event.artistNotificationSentAt ? "#D1FAE5" : "#BAE6FD"}
                          textColor={event.artistNotificationSentAt ? "#047857" : "#0369A1"}
                          onClick={() => setPushModalEvent(event)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {events.length < count && (
            <div className="flex justify-center mt-6">
              <KButton
                text="Load more"
                height="38px"
                width="130px"
                background="#F5F5F5"
                hoverBackground="#E5E5E5"
                onClick={() => fetchEvents(offset + limit, true)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MarketingMilestones
